import { useState } from "react";
import { toast } from "sonner";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Send,
  Link2,
  Check,
} from "lucide-react";

const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const PinterestIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.48 2 2 6.48 2 12c0 4.08 2.46 7.58 5.99 9.13-.08-.78-.16-1.97.03-2.82.17-.72 1.1-4.59 1.1-4.59s-.28-.56-.28-1.39c0-1.3.75-2.27 1.69-2.27.8 0 1.18.6 1.18 1.31 0 .8-.51 2-.77 3.11-.22.92.46 1.67 1.37 1.67 1.65 0 2.92-1.74 2.92-4.25 0-2.22-1.6-3.78-3.88-3.78-2.64 0-4.2 1.98-4.2 4.03 0 .8.31 1.66.69 2.12.08.09.09.17.07.27-.07.31-.24.99-.27 1.13-.04.18-.15.22-.34.13-1.25-.58-2.03-2.4-2.03-3.87 0-3.15 2.29-6.04 6.6-6.04 3.47 0 6.16 2.47 6.16 5.77 0 3.45-2.17 6.22-5.19 6.22-1.01 0-1.97-.53-2.29-1.15l-.62 2.37c-.23.87-.84 1.96-1.25 2.62.94.29 1.94.45 2.97.45 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
  </svg>
);

export const ShareButtons = ({ url, title, imageUrl, testidPrefix = "share" }) => {
  const [copied, setCopied] = useState(false);
  const e = encodeURIComponent;

  const targets = [
    { key: "whatsapp", label: "WhatsApp", icon: <WhatsAppIcon size={17} />, href: `https://wa.me/?text=${e(`${title} — ${url}`)}` },
    { key: "twitter", label: "Twitter / X", icon: <Twitter size={16} />, href: `https://twitter.com/intent/tweet?text=${e(title)}&url=${e(url)}` },
    { key: "facebook", label: "Facebook", icon: <Facebook size={16} />, href: `https://www.facebook.com/sharer/sharer.php?u=${e(url)}` },
    { key: "instagram", label: "Instagram", icon: <Instagram size={16} />, href: "https://www.instagram.com" },
    { key: "telegram", label: "Telegram", icon: <Send size={16} />, href: `https://t.me/share/url?url=${e(url)}&text=${e(title)}` },
    { key: "linkedin", label: "LinkedIn", icon: <Linkedin size={16} />, href: `https://www.linkedin.com/sharing/share-offsite/?url=${e(url)}` },
    { key: "pinterest", label: "Pinterest", icon: <PinterestIcon size={16} />, href: `https://pinterest.com/pin/create/button/?url=${e(url)}&description=${e(title)}${imageUrl ? `&media=${e(imageUrl)}` : ""}` },
  ];

  const copyLink = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      toast.success("Tautan artikel disalin ke clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Gagal menyalin tautan.");
    }
  };

  return (
    <div data-testid={`${testidPrefix}-buttons`} className="flex flex-wrap items-center gap-3">
      <span className="mr-1 text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">
        Bagikan
      </span>
      {targets.map((t) => (
        <a
          key={t.key}
          href={t.href}
          target="_blank"
          rel="noopener noreferrer"
          title={`Bagikan ke ${t.label}`}
          aria-label={`Bagikan ke ${t.label}`}
          data-testid={`${testidPrefix}-${t.key}`}
          className="flex h-10 w-10 items-center justify-center border border-white/15 text-[#A3A3A3] transition-all duration-300 hover:-translate-y-0.5 hover:border-gold hover:bg-gold hover:text-ink"
        >
          {t.icon}
        </a>
      ))}
      <button
        onClick={copyLink}
        title="Salin tautan"
        aria-label="Salin tautan artikel"
        data-testid={`${testidPrefix}-copy-link`}
        className={`flex h-10 w-10 items-center justify-center border transition-all duration-300 hover:-translate-y-0.5 ${
          copied
            ? "border-gold bg-gold text-ink"
            : "border-white/15 text-[#A3A3A3] hover:border-gold hover:bg-gold hover:text-ink"
        }`}
      >
        {copied ? <Check size={16} /> : <Link2 size={16} />}
      </button>
    </div>
  );
};
